import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Briefcase, MapPin, DollarSign, Clock, ArrowLeft, Loader2, CalendarClock } from 'lucide-react';
import { Button } from '../components/ui/button';
import MultiStepApplicationForm from '../components/MultiStepApplicationForm';
import { usePageTitle } from '../components/PageTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Clean HTML content: remove problematic characters and formatting
const cleanHtml = (html) => {
  if (!html) return '';
  
  let cleaned = html;
  
  // Replace non-breaking spaces with regular spaces
  cleaned = cleaned.replace(/\u00A0/g, ' ');
  cleaned = cleaned.replace(/&nbsp;/gi, ' ');
  
  // Remove soft hyphens (U+00AD and &shy;)
  cleaned = cleaned.replace(/\u00AD/g, '');
  cleaned = cleaned.replace(/&shy;/gi, '');
  
  // Remove zero-width spaces (U+200B)
  cleaned = cleaned.replace(/\u200B/g, '');
  
  // Remove zero-width non-joiner (U+200C)
  cleaned = cleaned.replace(/\u200C/g, '');
  
  // Remove zero-width joiner (U+200D)
  cleaned = cleaned.replace(/\u200D/g, '');
  
  // Remove word joiner (U+2060)
  cleaned = cleaned.replace(/\u2060/g, '');
  
  // Remove <wbr> tags (word break opportunity)
  cleaned = cleaned.replace(/<wbr\s*\/?>/gi, '');
  
  // Remove inline styles that might cause word breaks
  cleaned = cleaned.replace(/style\s*=\s*["'][^"']*word-break[^"']*["']/gi, '');
  cleaned = cleaned.replace(/style\s*=\s*["'][^"']*overflow-wrap[^"']*["']/gi, '');
  
  // Remove multiple consecutive br tags
  cleaned = cleaned.replace(/(<br\s*\/?>\s*){2,}/gi, '<br>');
  
  // Remove br tags at the start/end of paragraphs
  cleaned = cleaned.replace(/<p>\s*<br\s*\/?>\s*/gi, '<p>');
  cleaned = cleaned.replace(/\s*<br\s*\/?>\s*<\/p>/gi, '</p>');
  
  // Remove empty paragraphs
  cleaned = cleaned.replace(/<p>\s*<\/p>/gi, '');
  cleaned = cleaned.replace(/<p><br\s*\/?><\/p>/gi, '');
  
  return cleaned;
};

// Sanitize and render HTML content
const RichTextContent = ({ html, className = '' }) => {
  const sanitizedHtml = useMemo(() => {
    if (!html) return '';
    
    // First clean the HTML of problematic characters
    const cleaned = cleanHtml(html);
    
    // Then sanitize - allow common formatting tags including links
    // Strip all style attributes to prevent inline word-break styles
    const clean = DOMPurify.sanitize(cleaned, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'span', 'a'],
      ALLOWED_ATTR: ['class', 'href', 'target', 'rel'],
      FORBID_ATTR: ['style']  // Remove all inline styles
    });
    return clean;
  }, [html]);

  if (!sanitizedHtml || sanitizedHtml === '<p><br></p>' || sanitizedHtml.trim() === '') return null;

  return (
    <div 
      className={`rich-text-content ${className}`}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

// For legacy plain text content, preserve line breaks but use normal wrapping
const PlainTextContent = ({ text, className = '' }) => {
  if (!text) return null;
  
  // If text contains HTML tags, render as rich text
  if (text.includes('<') && text.includes('>')) {
    return <RichTextContent html={text} className={className} />;
  }
  
  return (
    <div className={`plain-text-content ${className}`}>
      {text}
    </div>
  );
};

// For legacy array format (responsibilities/qualifications)
const LegacyListContent = ({ items, className = '' }) => {
  if (!items) return null;
  
  if (typeof items === 'string') {
    return <RichTextContent html={items} className={className} />;
  }
  
  if (Array.isArray(items) && items.length > 0) {
    return (
      <ul className={`space-y-2 ${className}`}>
        {items.map((item, index) => (
          <li key={index} className="flex items-start gap-2 text-gray-700">
            <span className="text-amber-500 mt-1 flex-shrink-0">•</span>
            <span className="break-words">{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  
  return null;
};

// Generate URL slug from job title
export const generateJobSlug = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

const JobDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  usePageTitle(job ? `${job.title} | Careers | Lehigh Human Support Services` : 'Job Details | Lehigh Human Support Services');

  useEffect(() => {
    fetchJob();
  }, [slug]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/public/jobs`);
      const activeJobs = response.data.filter(j => j.active !== false);
      
      // Find job by slug
      const foundJob = activeJobs.find(j => generateJobSlug(j.title) === slug);
      
      if (foundJob) {
        setJob(foundJob);
        setError(null);
      } else {
        setError('Job not found');
      }
    } catch (err) {
      console.error('Error fetching job:', err);
      setError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  // Check if a section has content
  const hasContent = (content) => {
    if (!content) return false;
    if (typeof content === 'string') {
      const stripped = content.replace(/<[^>]*>/g, '').trim();
      return stripped.length > 0;
    }
    if (Array.isArray(content)) return content.length > 0;
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen">
        <div className="container mx-auto px-4 py-20 text-center">
          <Briefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{error || 'Job Not Found'}</h1>
          <p className="text-gray-600 mb-8">The job posting you're looking for doesn't exist or has been removed.</p>
          <Link to="/careers">
            <Button className="bg-amber-500 hover:bg-amber-600">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Careers
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-white py-12 lg:py-16">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Back Link */}
            <Link 
              to="/careers" 
              className="inline-flex items-center text-amber-600 hover:text-amber-700 mb-6 transition-colors"
              data-testid="back-to-careers"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Careers
            </Link>

            {/* Job Title */}
            <h1 className="text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-6">
              {job.title}
            </h1>

            {/* Job Meta Info */}
            <div className="flex flex-wrap gap-4 lg:gap-6 text-gray-600">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <span className="font-medium">{job.type}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-amber-500" />
                <span>{job.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-amber-500" />
                <span className="font-semibold text-amber-600">{job.salary}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Content - Full width, no card */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="job-page-content">
              {/* Apply Now Button - Top */}
              <div className="mb-8 pb-8 border-b">
                <Button 
                  onClick={() => setShowApplicationForm(true)}
                  className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-lg px-8 py-6"
                  data-testid="apply-now-top"
                >
                  <Briefcase className="w-5 h-5 mr-2" />
                  Apply Now
                </Button>
              </div>

              {/* Available Shifts - displayed before job description */}
              {job.available_shifts && job.available_shifts.trim() && (
                <div className="mb-8 pb-8 border-b" data-testid="available-shifts-section">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-amber-500 flex items-center gap-2">
                    <CalendarClock className="w-6 h-6 text-amber-500" />
                    Available Shifts
                  </h2>
                  <ul className="space-y-2">
                    {job.available_shifts.split('\n').filter(shift => shift.trim()).map((shift, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-700">
                        <span className="text-amber-500 mt-1 flex-shrink-0">•</span>
                        <span className="shift-text">{shift.trim()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

                {/* Job Sections */}
                <div className="space-y-10 job-content" style={{ minWidth: 0, maxWidth: '100%', width: '100%' }}>
                  {/* 1. Job Description */}
                  {hasContent(job.job_description) && (
                    <div className="job-section">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-amber-500">
                        Job Description
                      </h2>
                      <RichTextContent html={job.job_description} className="text-gray-700 leading-relaxed" />
                    </div>
                  )}

                  {/* 2. About This Role */}
                  {hasContent(job.description) && (
                    <div className="job-section">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-amber-500">
                        About This Role
                      </h2>
                      <PlainTextContent text={job.description} className="text-gray-700 leading-relaxed" />
                    </div>
                  )}

                  {/* 3. Why Join LHSS */}
                  {hasContent(job.why_join_lhss) && (
                    <div className="job-section">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-amber-500">
                        Why Join LHSS
                      </h2>
                      <RichTextContent html={job.why_join_lhss} className="text-gray-700 leading-relaxed" />
                    </div>
                  )}
                  
                  {/* 4. Responsibilities */}
                  {hasContent(job.responsibilities) && (
                    <div className="job-section">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-amber-500">
                        Responsibilities
                      </h2>
                      <LegacyListContent items={job.responsibilities} className="text-gray-700" />
                    </div>
                  )}
                  
                  {/* 5. Qualifications */}
                  {hasContent(job.qualifications) && (
                    <div className="job-section">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-amber-500">
                        Qualifications
                      </h2>
                      <LegacyListContent items={job.qualifications} className="text-gray-700" />
                    </div>
                  )}

                  {/* 6. Additional Information */}
                  {hasContent(job.additional_info) && (
                    <div className="job-section">
                      <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-amber-500">
                        Additional Information
                      </h2>
                      <RichTextContent html={job.additional_info} className="text-gray-700 leading-relaxed" />
                    </div>
                  )}
                </div>

                {/* Apply Now Button - Bottom */}
                <div className="mt-10 pt-8 border-t">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button 
                      onClick={() => setShowApplicationForm(true)}
                      className="flex-1 sm:flex-none bg-amber-500 hover:bg-amber-600 text-lg px-8 py-6"
                      data-testid="apply-now-bottom"
                    >
                      <Briefcase className="w-5 h-5 mr-2" />
                      Apply Now
                    </Button>
                    <Link to="/careers" className="flex-1 sm:flex-none">
                      <Button 
                        variant="outline" 
                        className="w-full border-gray-300 text-gray-700 hover:bg-gray-100 text-lg px-8 py-6"
                      >
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Back to Careers
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Application Form Modal */}
      {showApplicationForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Apply for {job.title}</h2>
                <Button 
                  variant="ghost" 
                  onClick={() => setShowApplicationForm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </Button>
              </div>
              <MultiStepApplicationForm 
                job={job} 
                onClose={() => setShowApplicationForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Rich text content styling - normal word wrapping, no mid-word breaks */}
      <style>{`
        /* Job page content - full width, no card */
        .job-page-content {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          box-sizing: border-box;
        }
        
        /* Reset ALL word-breaking on job content */
        .job-content,
        .job-content *,
        .job-section,
        .job-section *,
        .rich-text-content,
        .rich-text-content *,
        .plain-text-content,
        .plain-text-content * {
          word-break: normal !important;
          overflow-wrap: normal !important;
          hyphens: none !important;
          -webkit-hyphens: none !important;
          -ms-hyphens: none !important;
        }
        
        /* Container constraints */
        .job-content,
        .job-section,
        .rich-text-content,
        .plain-text-content {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          white-space: normal !important;
        }
        
        /* Paragraphs */
        .rich-text-content p,
        .plain-text-content,
        .job-content p {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: normal !important;
          hyphens: none !important;
          margin-bottom: 1rem;
          line-height: 1.6;
          font-weight: 400;
          font-style: normal;
        }
        
        /* List items */
        .rich-text-content li,
        .job-content li {
          display: list-item !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: normal !important;
          hyphens: none !important;
          margin-bottom: 0.5rem;
          line-height: 1.6;
        }
        
        /* Inline elements */
        .rich-text-content span,
        .rich-text-content strong,
        .rich-text-content b,
        .rich-text-content em,
        .rich-text-content i,
        .rich-text-content u,
        .job-content span,
        .job-content strong,
        .job-content em {
          display: inline !important;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: normal !important;
        }
        
        /* Headings */
        .rich-text-content h1,
        .rich-text-content h2,
        .rich-text-content h3,
        .rich-text-content h4 {
          font-weight: 700;
          font-style: normal;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          color: #1f2937;
          white-space: normal !important;
          word-break: normal !important;
          overflow-wrap: normal !important;
          max-width: 100% !important;
        }
        .rich-text-content h1 { font-size: 1.5rem; }
        .rich-text-content h2 { font-size: 1.25rem; }
        .rich-text-content h3 { font-size: 1.125rem; }
        .rich-text-content h4 { font-size: 1rem; }
        
        /* Bold text */
        .rich-text-content strong,
        .rich-text-content b {
          font-weight: 600;
          color: #1f2937;
        }
        
        /* Italic text */
        .rich-text-content em,
        .rich-text-content i {
          font-style: italic;
          font-weight: inherit;
        }
        
        /* Underline */
        .rich-text-content u {
          text-decoration: underline;
        }
        
        /* Links - only links can break at any point to prevent overflow */
        .rich-text-content a {
          color: #d97706;
          text-decoration: underline;
          word-break: break-all !important;
        }
        
        .rich-text-content a:hover {
          color: #b45309;
        }
        
        /* Lists */
        .rich-text-content ul,
        .rich-text-content ol {
          margin-left: 1.5rem;
          margin-bottom: 1rem;
          padding-left: 0.5rem;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }
        
        .rich-text-content ul {
          list-style-type: disc;
        }
        
        .rich-text-content ol {
          list-style-type: decimal;
        }
        
        .rich-text-content li::marker {
          color: #d97706;
        }
        
        /* BR tags */
        .rich-text-content br {
          display: block;
          content: "";
          margin-top: 0;
        }
        
        /* Plain text content */
        .plain-text-content {
          white-space: pre-wrap !important;
          line-height: 1.6;
          font-weight: 400;
          font-style: normal;
        }
        
        /* Shift text */
        .shift-text {
          font-weight: 400;
          font-style: normal;
          line-height: 1.6;
          white-space: normal !important;
          word-break: normal !important;
        }
        
        /* Mobile responsive */
        @media (max-width: 768px) {
          .rich-text-content ul,
          .rich-text-content ol {
            margin-left: 1rem;
          }
        }
        
        /* Quill editor specific resets - the editor may add ql-* classes */
        .ql-editor,
        .ql-editor *,
        [class^="ql-"],
        [class*=" ql-"] {
          word-break: normal !important;
          overflow-wrap: normal !important;
          hyphens: none !important;
        }
      `}</style>
    </div>
  );
};

export default JobDetails;
